/*
 * Velkomstpopup: question -> email -> sms (native Omnisend embedded form,
 * two steps) -> the offer. Markup, the offer versions and the rules for who
 * never sees it: snippets/dbk-signup-popup.liquid.
 * Visitor state lives in localStorage/sessionStorage and holds no contact data.
 * Already subscribed (Kasper 2026-10-06): a visitor whose email is already on the list gets a thank-you
 * instead of a code (TUSINDTAK10 and GAVEBLOK are once per customer), for all three versions: see watchSubscribe().
 * Personal code (only when the theme setting "Kodeservice" is filled in): on the first quiz click the popup asks the
 * code service dbk-welcome-code for this visitor's own 7-day code (A/B: offer "percent" -> DBK-XXXX-XXXX, C: offer
 * "gift" -> GAVE-XXXX-XXXX), puts it in two hidden Omnisend fields before the email is sent and shows/applies it at
 * the end, but ONLY if the email step's request to Omnisend really carried it (single-code gate: a customer never sees
 * two codes). Otherwise, and with a blank setting, it is the version's shared code ("Rabatkode" / "Gavekode"), as before.
 * Add ?dbk-popup=1 to any URL to clear that state and open the popup at once,
 * and &dbk-arm=a|b|c to pick the offer version.
 */
if (!customElements.get('dbk-signup-popup')) {
  const STORE_KEY = 'dbk-signup-v1'; // { status: 'subscribed' | 'known' | 'dismissed', at }
  const SESSION_KEY = 'dbk-signup-session'; // { firstSeen, shown, teaser }
  const ARM_KEY = 'dbk-signup-arm'; // the offer version this browser keeps for the whole test
  const DAY = 86400000;
  const PRELOAD_LEAD = 3000; // ms before the open the images start downloading
  const IMAGE_WAIT = 800; // ms the open waits for an image that is still on its way, never longer
  // Values of the hidden "popup_tilbud" radio in the Omnisend form.
  const ARM_VALUES = { a: 'A 10 procent', b: 'B Vil du have rabat', c: 'C Gratis gave' };
  const SUBMIT_TEXT = { a: 'Hent min rabat', b: 'Se min rabat', c: 'Hent min gave' };
  const CART_SECTIONS = 'cart-items,cart-footer,cart-item-count,cart-live-region';
  const CODE_KEY = 'dbk-welcome-code'; // { offer, code, expires_at, expires_label } of this browser's personal code
  const VID_KEY = 'dbk-welcome-vid'; // random visitor id sent to the code service (never contact data)
  const CODE_TIMEOUT = 4000; // ms per request; the service is asked twice, then the backup code is used
  // The offer each version asks the service for, and the shape of the code it must answer with (the service's alphabet: no 0/O/1/I).
  const ARM_OFFER = { a: 'percent', b: 'percent', c: 'gift' };
  const CODE_PATTERN = { percent: /^DBK-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/, gift: /^GAVE-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/ };
  const LABEL_MAX = 30; // characters of the expiry label
  // The two Omnisend properties that carry the personal code onto the contact. Omnisend names the form inputs, and the
  // keys of the formData it POSTs, custom_<property>.
  const FIELD_CODE = 'custom_velkomst_kode';
  const FIELD_EXPIRY = 'custom_velkomst_kode_udloeber';
  const VID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/; // lower case: the service wants the canonical form
  // The one Omnisend call the popup watches: every form step POSTs here (read from Omnisend's forms script, main.js).
  const SUBSCRIBE_URL = /^https:\/\/[^/]*(soundest|omnisend)[^/]*\/REST\/forms\/v2\/subscribe(\?|$)/i;
  const readStore = (storage, key) => {
    try { return JSON.parse(storage.getItem(key)) || {}; } catch (error) { return {}; }
  };
  const writeStore = (storage, key, value) => {
    try { storage.setItem(key, JSON.stringify(value)); } catch (error) { /* private mode: state just isn't kept */ }
  };
  const hasCookie = name => document.cookie.split('; ').some(entry => entry.startsWith(name + '='));
  // Microsoft Clarity is on the store: custom events give the step funnel (no contact data).
  const track = (...args) => { try { window.clarity?.(...args); } catch (error) { /* analytics never blocks the popup */ } };
  const readRaw = key => { try { return localStorage.getItem(key); } catch (error) { return null; } };
  const writeRaw = (key, value) => { try { localStorage.setItem(key, value); } catch (error) { /* private mode: it just isn't kept */ } };
  const readCache = () => { try { return JSON.parse(readRaw(CODE_KEY)); } catch (error) { return null; } };
  const newVid = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
  };
  // The shape the code service promises for that offer: a DBK- (percent) or GAVE- (gift) code, a future expiry and a
  // short plain-text label in Danish words ("13. oktober"). The label is only ever inserted as text, but anything that
  // looks like HTML is refused too. A code of the other offer never passes, so a percent code can never reach a C
  // visitor or a gift code an A/B visitor.
  const validCode = (value, offer) => Boolean(value) && typeof value === 'object' && Boolean(CODE_PATTERN[offer])
    && typeof value.code === 'string' && CODE_PATTERN[offer].test(value.code)
    && typeof value.expires_label === 'string' && value.expires_label.trim().length > 0 && value.expires_label.length <= LABEL_MAX && !/[<>]/.test(value.expires_label)
    && typeof value.expires_at === 'string' && Date.parse(value.expires_at) > Date.now();
  // Calendar day in Copenhagen as a whole number (date only, no time of day), for comparing cached codes by day.
  const copenhagenDay = date => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Copenhagen', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
      const part = type => Number(parts.find(entry => entry.type === type).value);
      return Date.UTC(part('year'), part('month') - 1, part('day')) / DAY;
    } catch (error) {
      return NaN; // no Intl time zones or an unreadable date: nothing counts as "from today", a fresh code is asked for
    }
  };
  // One try at the service, at most CODE_TIMEOUT ms. text/plain keeps it a "simple" CORS request (no preflight round
  // trip); the service does not look at Content-Type. 'gone' = 410: that visitor id's code is used or has expired.
  // Anything but a valid 200 answer for this offer counts as a failure (null).
  const requestCode = async (endpoint, vid, offer) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CODE_TIMEOUT);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ vid, offer }),
        signal: controller.signal,
      });
      if (response.status === 410) return 'gone';
      if (response.status !== 200) return null;
      const data = await response.json();
      return validCode(data, offer) ? { offer, code: data.code, expires_at: data.expires_at, expires_label: data.expires_label } : null;
    } catch (error) {
      return null; // network error, timeout (abort) or an unreadable body
    } finally {
      clearTimeout(timer);
    }
  };
  const postJSON = (url, body) => fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  customElements.define('dbk-signup-popup', class extends HTMLElement {
    connectedCallback() {
      if (this.dialog) return;
      this.dialog = this.querySelector('dialog');
      this.formId = this.dataset.formId;
      // Empty = no personal codes: the popup shows and applies the shared codes, as before.
      this.endpoint = /^https:\/\//i.test((this.dataset.codeEndpoint || '').trim()) ? this.dataset.codeEndpoint.trim() : '';
      this.embed = this.querySelector('[data-embed]');
      this.loading = this.querySelector('[data-loading]');
      this.loadError = this.querySelector('[data-load-error]');
      this.teaser = this.querySelector('[data-teaser]');
      this.step = 'question';
      this.desktop = window.matchMedia('(min-width: 768px)');

      const params = new URLSearchParams(window.location.search);
      const forced = params.get('dbk-popup') === '1';
      if (forced) {
        try { localStorage.removeItem(STORE_KEY); sessionStorage.removeItem(SESSION_KEY); } catch (error) { /* ignore */ }
      }
      this.arm = this.pickArm(params.get('dbk-arm'));
      const stored = readStore(localStorage, STORE_KEY);
      this.session = readStore(sessionStorage, SESSION_KEY);
      if (!this.session.firstSeen) {
        this.session.firstSeen = Date.now();
        this.saveSession();
      }

      if (!forced) {
        if (stored.status === 'subscribed' || stored.status === 'known') return this.remove();
        // Already on the list: arrived from one of our emails, or Omnisend knows the browser.
        if (params.get('utm_source') === 'omnisend' || hasCookie('omnisendContactID')) {
          writeStore(localStorage, STORE_KEY, { status: 'known', at: Date.now() });
          return this.remove();
        }
        // They carry a code from an ad or a landing page already; don't swap it for ours.
        if (hasCookie('discount_code')) return this.remove();
        const declinedRecently = stored.status === 'dismissed' && Date.now() - stored.at < Number(this.dataset.reshowDays) * DAY;
        if (declinedRecently || this.session.shown) {
          if (!this.session.teaser) return this.remove();
          this.listen();
          this.showTeaser();
          return;
        }
      }

      this.listen();
      const delay = forced ? 300 : Math.max(this.session.firstSeen + Number(this.dataset.delay) * 1000 - Date.now(), 1500);
      this.preloadTimer = setTimeout(() => this.loadImages(), Math.max(delay - PRELOAD_LEAD, 0));
      this.openTimer = setTimeout(() => this.tryOpen(), delay);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.embedObserver?.disconnect();
      clearTimeout(this.preloadTimer);
      clearTimeout(this.openTimer);
      clearTimeout(this.loadingTimeout);
    }

    // One version per browser for the whole test, chosen at random the first time.
    pickArm(requested) {
      const available = ['a', 'b'];
      if (this.dataset.giftVariant) available.push('c');
      if (available.includes(requested)) return requested;
      const mode = this.dataset.offerMode;
      if (mode !== 'test') return available.includes(mode) ? mode : 'a';
      let arm = (readStore(localStorage, ARM_KEY) || {}).arm;
      if (!available.includes(arm)) {
        const random = new Uint32Array(1);
        window.crypto.getRandomValues(random);
        arm = available[random[0] % available.length];
        writeStore(localStorage, ARM_KEY, { arm, at: Date.now() });
      }
      return arm;
    }

    listen() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog.dataset.arm = this.arm;
      this.querySelectorAll('[data-for-arm]').forEach(element => {
        element.hidden = !element.dataset.forArm.split(' ').includes(this.arm);
      });
      track('set', 'dbk_popup_arm', this.arm);
      this.querySelectorAll('[data-answer]').forEach(button => {
        button.addEventListener('click', () => this.answer(button.dataset.answer), options);
      });
      this.querySelector('[data-decline]').addEventListener('click', () => {
        track('event', 'dbk_popup_declined_' + this.step);
        this.dialog.close();
      }, options);
      this.querySelector('[data-skip-sms]').addEventListener('click', () => {
        track('event', 'dbk_popup_sms_skipped');
        this.finish();
      }, options);
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.querySelector('[data-copy]').addEventListener('click', () => this.copyCode(), options);
      this.querySelector('[data-retry]').addEventListener('click', () => window.location.reload(), options);
      this.querySelector('[data-teaser-open]').addEventListener('click', () => this.open(), options);
      this.querySelector('[data-teaser-close]').addEventListener('click', () => {
        this.session.teaser = false;
        this.saveSession();
        this.teaser.hidden = true;
      }, options);
      // Escape is the keyboard way out: a "no thanks" before the signup, "kun mail" on the sms step.
      this.dialog.addEventListener('cancel', event => {
        if (this.step === 'sms') {
          event.preventDefault();
          track('event', 'dbk_popup_sms_skipped');
          this.finish();
        } else if (this.step !== 'done') {
          track('event', 'dbk_popup_declined_' + this.step);
        }
      }, options);
      this.dialog.addEventListener('close', () => this.onClose(), options);

      this.watchSubscribe();
      // The personal code has to be in the form before the email is sent: capture the submit on the way down.
      this.embed.addEventListener('submit', event => this.holdSubmit(event), { ...options, capture: true });
      this.embedObserver = new MutationObserver(() => this.checkForm());
      this.embedObserver.observe(this.embed, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      window.addEventListener('omnisendForms', event => {
        const detail = event.detail || {};
        if (detail.form?.id !== this.formId) return;
        // Omnisend fires "submit" (and "stepSubmit") only after its API accepted
        // the data. Never read detail.formValues: the theme does not touch contact data.
        if (detail.type === 'submit' || (detail.type === 'stepSubmit' && String(detail.step) === '1')) this.emailDone();
        else if (detail.type === 'stepSubmit') {
          if (this.step === 'sms') track('event', 'dbk_popup_sms_submitted');
          this.finish();
        }
        else this.checkForm();
      }, options);
      this.checkForm();
    }

    blocked() {
      const modals = window.Alpine?.store?.('modals');
      if (modals && ['leftDrawer', 'rightDrawer', 'modal', 'popup'].some(name => modals[name]?.open)) return true;
      if (document.querySelector('dialog[open]')) return true;
      const active = document.activeElement;
      return Boolean(active && active.matches('input, textarea, select, [contenteditable="true"]'));
    }

    async tryOpen() {
      if (document.hidden || this.blocked()) {
        this.openTimer = setTimeout(() => this.tryOpen(), 1000);
        return;
      }
      this.loadImages();
      await this.imagesReady();
      if (!this.isConnected) return;
      // Something came up during the wait (a drawer, a field in focus, another tab): try again later.
      if (document.hidden || this.blocked()) {
        this.openTimer = setTimeout(() => this.tryOpen(), 1000);
        return;
      }
      this.open();
    }

    // Only the images this visitor will see: the photo on a desktop screen, never the gift of another version.
    loadImages() {
      this.querySelectorAll('img[data-src]').forEach(image => {
        if (image.closest('.dbk-signup__photo') && !this.desktop.matches) return;
        const version = image.closest('[data-for-arm]');
        if (version && !version.dataset.forArm.split(' ').includes(this.arm)) return;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
      });
    }

    // The banner and (on desktop) the photo are on screen from the first frame: when one is still downloading at open
    // time, wait for it so the panel is not empty, but never longer than IMAGE_WAIT.
    imagesReady() {
      const shown = [this.querySelector('.dbk-signup__banner'), this.desktop.matches ? this.querySelector('.dbk-signup__photo img') : null];
      const pending = shown.filter(image => image && image.getAttribute('src') && !image.complete);
      if (!pending.length) return Promise.resolve();
      return Promise.race([
        Promise.all(pending.map(image => image.decode().catch(() => {}))),
        new Promise(resolve => setTimeout(resolve, IMAGE_WAIT)),
      ]);
    }

    open() {
      if (this.dialog.open) return;
      this.loadImages();
      this.lastFocus = document.activeElement;
      this.teaser.hidden = true;
      this.session.shown = true;
      this.saveSession();
      this.dialog.showModal();
      track('event', 'dbk_popup_shown');
      track('event', 'dbk_popup_shown_' + this.arm);
      this.go(this.step);
    }

    go(step) {
      this.step = step;
      this.dialog.dataset.step = step;
      this.querySelectorAll('[data-step-panel]').forEach(panel => {
        panel.hidden = !panel.dataset.stepPanel.split(' ').includes(step);
      });
      this.querySelectorAll('[data-for-step]').forEach(element => { element.hidden = element.dataset.forStep !== step; });
      // B reveals the amount only after the signup. Already subscribed: the thank-you instead of any offer.
      const offer = this.thanks ? 'thanks' : (this.arm === 'b' && step === 'done' ? 'b-done' : this.arm);
      this.querySelectorAll('[data-offer]').forEach(element => { element.hidden = element.dataset.offer !== offer; });
      this.querySelector('[data-code-block]').hidden = Boolean(this.thanks);
      this.querySelector('[data-step-footer]').hidden = step !== 'email'; // the sms step carries Omnisend's own consent text
      this.querySelector('[data-decline]').hidden = step !== 'question' && step !== 'email';
      this.querySelector('[data-skip-sms]').hidden = step !== 'sms';
      this.dialog.scrollTop = 0;
      if (step === 'email') {
        track('event', 'dbk_popup_email_step');
        this.checkForm();
        const input = this.embed.querySelector('input[type="email"]');
        if (input) {
          input.focus();
        } else {
          this.loading.hidden = false;
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = setTimeout(() => {
            if (this.step === 'email' && !this.embed.querySelector('input[type="email"]')) {
              this.loading.hidden = true;
              this.loadError.hidden = false;
            }
          }, 12000);
        }
      } else if (step === 'sms') {
        track('event', 'dbk_popup_sms_step');
        this.checkForm();
        this.embed.querySelector('input[type="tel"]')?.focus();
      } else if (step === 'done') {
        this.querySelector(this.thanks || this.arm === 'c' ? '[data-close]' : '[data-copy]').focus({ preventScroll: true });
      } else {
        this.querySelector('#DbkSignupOffer').focus({ preventScroll: true });
      }
    }

    answer(value) {
      this.answerValue = value;
      this.startCode();
      this.selectRadios();
      track('set', 'dbk_popup_answer', value);
      track('event', 'dbk_popup_answered');
      this.go('email');
    }

    // The quiz answer and the offer version are radio fields in the Omnisend
    // form, hidden by the theme and ticked here, so both land on the contact
    // as properties (holde_frisk, popup_tilbud).
    selectRadios() {
      const radios = Array.from(this.embed.querySelectorAll('input[type="radio"]'));
      [this.answerValue, ARM_VALUES[this.arm]].filter(Boolean).forEach(value => {
        const wanted = value.trim().toLowerCase();
        const radio = radios.find(input => {
          const label = input.closest('label') || (input.id && this.embed.querySelector('label[for="' + input.id + '"]'));
          return input.value.trim().toLowerCase() === wanted || (label?.textContent || '').trim().toLowerCase() === wanted;
        });
        if (radio && !radio.checked) radio.click();
      });
    }

    // The personal code. Started on the first quiz click, so it is ready long before the email is typed.
    // The promise resolves to { offer, code, expires_at, expires_label }, or null = backup mode (the version's
    // shared code). It never rejects.
    startCode() {
      if (!this.endpoint) return Promise.resolve(null);
      if (!this.codePromise) {
        this.codePending = true;
        const offer = ARM_OFFER[this.arm];
        this.codePromise = this.loadCode(offer).then(personal => {
          this.personal = personal;
          this.codePending = false;
          track('event', (offer === 'gift' ? 'dbk_popup_gift_' : 'dbk_popup_code_') + (personal ? 'personal' : 'backup'));
          this.fillCodeFields();
          return personal;
        });
      }
      return this.codePromise;
    }

    async loadCode(offer) {
      try {
        // The service ends a code 7 days after the day it was made (Copenhagen time), so a cached code is only good
        // while it is for this version's offer and the Copenhagen date of its expires_at is today + 7 (dates, not
        // times). A code from an earlier day, for the other offer (a version forced with ?dbk-arm=), an expired or an
        // unreadable cache starts over with a NEW visitor id, so the fresh code is always minted today.
        const hadCache = readRaw(CODE_KEY) !== null;
        const cached = readCache();
        if (cached?.offer === offer && validCode(cached, offer) && copenhagenDay(new Date(cached.expires_at)) >= copenhagenDay(new Date()) + 7) {
          return { offer, code: cached.code, expires_at: cached.expires_at, expires_label: cached.expires_label };
        }
        let vid = readRaw(VID_KEY);
        if (!VID_PATTERN.test(vid || '') || hadCache) {
          vid = newVid();
          writeRaw(VID_KEY, vid);
        }
        let result = await requestCode(this.endpoint, vid, offer);
        if (!result || result === 'gone') {
          if (result === 'gone') {
            vid = newVid(); // that vid's code is used or expired: drop it. The new vid makes this the one retry.
            writeRaw(VID_KEY, vid);
          }
          result = await requestCode(this.endpoint, vid, offer);
        }
        if (result === 'gone') {
          try { localStorage.removeItem(VID_KEY); } catch (error) { /* private mode */ }
          result = null;
        }
        if (result) writeRaw(CODE_KEY, JSON.stringify(result));
        return result;
      } catch (error) {
        return null; // whatever went wrong, the popup falls back to the backup code
      }
    }

    // The two hidden text fields of the Omnisend form copy that carry the personal code onto the contact
    // (properties velkomst_kode and velkomst_kode_udloeber). Same technique as selectRadios: found by the
    // field's name, else by its label, aria-label or placeholder text. The live form has neither field, so
    // nothing is found and nothing happens.
    // selector: the exact Omnisend DOM name; swap it here if the field is ever recreated
    findCodeField() { return this.findTextField(FIELD_CODE, ['velkomst_kode', 'velkomstkode', 'kode']); }

    // selector: the exact Omnisend DOM name; swap it here if the field is ever recreated
    findExpiryField() { return this.findTextField(FIELD_EXPIRY, ['velkomst_kode_udloeber', 'kode udløber', 'dato']); }

    findTextField(name, texts) {
      const inputs = Array.from(this.embed.querySelectorAll('input[type="text"], input:not([type])'));
      const same = (a, b) => (a || '').trim().toLowerCase() === b;
      return inputs.find(input => same(input.name, name))
        || inputs.find(input => {
          const label = input.closest('label') || (input.id && this.embed.querySelector('label[for="' + CSS.escape(input.id) + '"]'));
          return [label?.textContent, input.getAttribute('aria-label'), input.placeholder].some(text => texts.some(wanted => same(text, wanted)));
        }) || null;
    }

    // Hides both fields like the radios (always: with the form copy selected and the setting blank they must
    // never show) and fills them with the personal code, BOTH or NONE: one input alone never gets a code, so a
    // personal code can only leave in the form together with its expiry. In backup mode, with only one input in
    // the form, or with the setting blank, they stay empty (and anything in them is emptied).
    fillCodeFields() {
      const code = this.findCodeField();
      const expiry = this.findExpiryField();
      const both = Boolean(this.endpoint && this.personal && code && expiry);
      [[code, both && this.personal.code], [expiry, both && this.personal.expires_label]].forEach(([input, value]) => {
        if (!input) return;
        this.embed.querySelectorAll('div[class*="-field-container-"]').forEach(box => {
          if (box.contains(input) && !box.hidden) box.hidden = true;
        });
        // Once the email request has gone out the fields are left alone (checkFields lifts this for the next attempt).
        if (this.emailSent) return;
        // The native setter plus both events, so Omnisend gets the value whether it reads the DOM or listens to input.
        if (input.value !== (value || '')) {
          Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set.call(input, value || '');
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // Single-code gate, part 1 (the DOM, on the way into the email submit, before Omnisend reads the form): both inputs
    // are there and hold exactly the personal code and its label, or both are empty. Whatever the popup will show, the
    // contact must not get a code that differs from it.
    checkFields() {
      this.emailSent = false; // a new attempt: verify (and if need be fill or empty) the fields again
      this.fillCodeFields();
      const code = this.findCodeField();
      const expiry = this.findExpiryField();
      this.fieldsOk = Boolean(this.personal && code && expiry && code.value === this.personal.code && expiry.value === this.personal.expires_label);
    }

    // Single-code gate, part 2 (what really left): the personal code is shown only if the service gave one AND the email
    // step's request to Omnisend carried both properties with exactly its code and label. If that request could not be read
    // (sentFields unset), the DOM check at submit decides. Every other case shows the backup code and sent nothing.
    singleCode(personal) {
      if (!personal) return false;
      if (this.sentFields) return this.sentFields.code === personal.code && this.sentFields.label === personal.expires_label;
      return this.fieldsOk === true;
    }

    // If the code request has not finished when the email is submitted, hold the submit until it has
    // (at most two tries of 4 s), fill the two fields, then send it again exactly as Omnisend would have.
    holdSubmit(event) {
      if (!this.endpoint) return;
      const form = event.target;
      if (!form.querySelector?.('input[type="email"]')) return; // the code fields live on the email step only
      if (!this.codePromise || !this.codePending) return this.checkFields();
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.holding) return;
      this.holding = true;
      const submitter = event.submitter;
      form.setAttribute('aria-busy', 'true');
      this.codePromise.then(() => {
        this.holding = false;
        form.removeAttribute('aria-busy');
        // Same submit as the visitor's own, with the same button (the check runs when it comes back through here).
        // requestSubmit is missing before Safari 16: click instead.
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
        else (submitter || form.querySelector('button[type="submit"]'))?.click();
      });
    }

    // Already-subscribed visitors, step 1. Omnisend's form script POSTs every step to /REST/forms/v2/subscribe and
    // decides its own "already subscribed" section from ONE field of the JSON answer: formState, exactly the string
    // "subscribed" (anything else, or nothing, shows its success section). Omnisend only reads it after the last
    // step, so the popup reads the same field from the answer to the EMAIL step.
    // This is a pass-through wrapper scoped to that one URL and to the open email step: the request goes out untouched,
    // a rejection reaches the caller unchanged, and the caller gets the very same Response object. The only difference:
    // the answer is handed on after the popup has read a clone of it, so the thank-you is up before Omnisend moves its
    // own form to the phone step. Any problem in here is swallowed and the flow simply goes on as before.
    watchSubscribe() {
      if (this.watching || typeof window.fetch !== 'function') return;
      this.watching = true;
      const nativeFetch = window.fetch;
      const popup = this;
      window.fetch = function (input) {
        const result = nativeFetch.apply(this, arguments);
        try {
          const url = typeof input === 'string' ? input : ((input && (input.url || input.href)) || '');
          // Only the email step of an open popup: the back-in-stock form posts to the same URL.
          if (!SUBSCRIBE_URL.test(url) || !popup.isConnected || !popup.dialog.open || popup.step !== 'email') return result;
          if (popup.endpoint) popup.readSentFields(arguments[1]); // the single-code gate: personal-code mode only
          return result.then(async response => {
            try { popup.subscribeAnswered(await response.clone().json()); } catch (error) { /* not JSON: nothing to see */ }
            return response;
          });
        } catch (error) { /* watching never breaks the form */ }
        return result;
      };
    }

    // What the email step's request carries in the two code properties. ONLY those two keys are read from the body
    // (nothing else in it is looked at or kept). Unreadable body: sentFields stays unset and the DOM check decides.
    readSentFields(init) {
      this.emailSent = true;
      this.sentFields = undefined;
      try {
        const formData = JSON.parse(init.body).formData;
        if (formData && typeof formData === 'object') this.sentFields = { code: formData[FIELD_CODE], label: formData[FIELD_EXPIRY] };
      } catch (error) { /* not a JSON string: sentFields stays unset */ }
    }

    subscribeAnswered(data) {
      if (!data || typeof data !== 'object' || data.error) return;
      // What Omnisend sent at step 1, as a tag (a short word such as "success"), so the live behaviour can be checked in Clarity.
      const state = typeof data.formState === 'string' ? data.formState : (data.formState == null ? String(data.formState) : 'other');
      track('set', 'dbk_popup_step1_state', state.replace(/[^a-z]/gi, '').slice(0, 20) || 'empty');
      if (data.formState === 'subscribed') this.knownSubscriber();
    }

    // The email is already on the list: a thank-you right away. No code, nothing applied to the cart, no phone step.
    // The email step's request has already gone, so the contact is not touched again: the phone step never shows.
    knownSubscriber() {
      if (this.step === 'done') return;
      writeStore(localStorage, STORE_KEY, { status: 'subscribed', at: Date.now() });
      this.session.teaser = false;
      this.saveSession();
      this.teaser.hidden = true;
      this.showThanks();
    }

    showThanks() {
      track('event', 'dbk_popup_already_subscribed');
      this.thanks = true;
      this.go('done');
    }

    // Fallback, after the last step: Omnisend shows its "already subscribed" section right after it fires its events, not before.
    omnisendSubscribed() {
      return Array.from(this.embed.querySelectorAll('[class$="-subscribed-sections-container"]'))
        .some(element => element.getClientRects().length > 0);
    }

    checkForm() {
      const email = this.embed.querySelector('input[type="email"]');
      if (!email) return;
      if (!email.dataset.dbkStyled) {
        email.dataset.dbkStyled = 'true';
        email.setAttribute('aria-label', 'Din mailadresse');
        email.setAttribute('inputmode', 'email');
        email.setAttribute('autocomplete', 'email');
        email.setAttribute('autocapitalize', 'none');
        email.placeholder = 'dig@eksempel.dk';
        this.addLabel(email, 'Din mailadresse');
        const submit = email.closest('form')?.querySelector('button[type="submit"]');
        if (submit && SUBMIT_TEXT[this.arm]) submit.textContent = SUBMIT_TEXT[this.arm];
      }
      const phone = this.embed.querySelector('input[type="tel"]');
      if (phone && !phone.dataset.dbkStyled) {
        phone.dataset.dbkStyled = 'true';
        phone.setAttribute('aria-label', 'Dit mobilnummer');
        phone.setAttribute('autocomplete', 'tel-national');
        this.addLabel(phone, 'Dit mobilnummer');
      }
      this.selectRadios();
      this.fillCodeFields();
      this.loading.hidden = true;
      this.loadError.hidden = true;
      clearTimeout(this.loadingTimeout);
      if ((this.step !== 'email' && this.step !== 'sms') || !this.dialog.open) return;
      // Backup for the events: Omnisend's own success/already-subscribed state became visible.
      const confirmed = Array.from(this.embed.querySelectorAll('[class$="-success-sections-container"], [class$="-subscribed-sections-container"]'))
        .some(element => element.getClientRects().length > 0);
      if (confirmed) this.finish();
    }

    addLabel(input, text) {
      if (this.embed.querySelector('label[for="' + input.id + '"]')) return;
      const label = document.createElement('label');
      label.className = 'dbk-signup__label';
      label.htmlFor = input.id;
      label.textContent = text;
      // A phone input sits in a flex row with the country prefix: the label goes above the whole row.
      (input.closest('[class$="-field-wrapper"]') || input).before(label);
    }

    // The email is on the list. Count it as a signup now, then ask for the phone number.
    emailDone() {
      if (this.step !== 'email') return;
      writeStore(localStorage, STORE_KEY, { status: 'subscribed', at: Date.now() });
      this.session.teaser = false;
      this.saveSession();
      track('event', 'dbk_popup_subscribed');
      track('event', 'dbk_popup_subscribed_' + this.arm);
      if (this.embed.querySelector('input[type="tel"]')) this.go('sms');
      else this.finish();
    }

    finish() {
      if (this.step === 'done' || this.finishing) return;
      writeStore(localStorage, STORE_KEY, { status: 'subscribed', at: Date.now() });
      this.session.teaser = false;
      this.saveSession();
      // Wait for the code request if it is still running (personal-code mode; started at the quiz answer, never
      // started here so no code is minted that the contact did not get), and one tick more so Omnisend has painted
      // its own "already subscribed" section (it comes after its events).
      this.finishing = true;
      (this.endpoint && this.codePromise ? this.codePromise : Promise.resolve(null)).then(personal => setTimeout(() => {
        this.finishing = false;
        if (this.step === 'done') return; // the email step's answer already said "subscribed"
        if (this.omnisendSubscribed()) return this.showThanks();
        if (this.endpoint && this.singleCode(personal)) return this.showDone(personal);
        // Backup mode (or no code service): the version's shared code, no expiry line. A service code that did NOT go out in
        // the form (a form without the inputs, values not sent) ends here too: the contact then has no personal code, so only
        // this one exists.
        if (personal) track('event', personal.offer === 'gift' ? 'dbk_popup_gift_gate_backup' : 'dbk_popup_code_gate_backup');
        this.showDone(null);
      }, 0));
    }

    // The end of the flow for a new subscriber: the code (or the gift), applied to the cart. A personal code comes with
    // its expiry line; the shared codes have none.
    showDone(personal) {
      const gift = this.arm === 'c';
      const code = personal ? personal.code : (gift ? this.dataset.giftCode : this.dataset.code) || '';
      this.activeCode = code;
      this.querySelectorAll('[data-expiry]').forEach(line => {
        line.hidden = !personal;
        line.textContent = personal ? 'Gælder til ' + personal.expires_label + ' kl. 23.59' : ''; // the label as the service wrote it, as text
      });
      // Personal-code mode, version C: the line names the code in use and says it is in the cart only once it is.
      const giftApplied = this.querySelector('[data-gift-applied]');
      const giftManual = this.querySelector('[data-gift-manual]');
      if (gift && this.endpoint && giftApplied && giftManual) {
        this.querySelectorAll('[data-gift-code-value]').forEach(value => { value.textContent = code; });
        giftApplied.hidden = true;
        giftManual.hidden = true;
      }
      if (!gift) {
        this.querySelector('[data-code-value]').textContent = code;
        this.querySelector('.dbk-signup__code').toggleAttribute('data-long', code.length > 11); // DBK-XXXX-XXXX is longer than TUSINDTAK10: CSS gives it room
      }
      this.go('done');
      if (gift) {
        this.applyGift(code).then(applied => {
          if (!this.endpoint || !giftApplied || !giftManual) return;
          giftApplied.hidden = !applied;
          giftManual.hidden = applied;
        });
      } else {
        this.applyCode(code).then(applied => {
          if (applied) this.querySelector('[data-applied]').textContent = 'Koden ligger allerede i din kurv.';
        });
      }
    }

    // /discount/<code> stores the code in the discount_code cookie, which checkout
    // applies; /cart/update.js puts it on the cart so the drawer shows it too.
    async applyCode(code) {
      if (!code) return false;
      try {
        await fetch('/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js', { credentials: 'same-origin' });
        await postJSON('/cart/update.js', { discount: code });
      } catch (error) {
        return false; // the visible code still works at checkout
      }
      return hasCookie('discount_code');
    }

    // Resolves to true when the code is on the cart. The cart is never emptied: the Blok is only added when it is missing.
    async applyGift(code) {
      const variant = Number(this.dataset.giftVariant);
      let applied = false;
      try {
        const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
        if (!cart.items.some(item => item.variant_id === variant)) {
          await postJSON('/cart/add.js', { items: [{ id: variant, quantity: 1 }] });
        }
        applied = await this.applyCode(code);
        // Refresh the drawer and the cart count without opening the drawer.
        const sections = await (await fetch(window.location.pathname + '?sections=' + CART_SECTIONS, { credentials: 'same-origin' })).json();
        document.body.dispatchEvent(new CustomEvent('shapes:modalcart:cartqtychange', { bubbles: true, detail: { response: { sections } } }));
        track('event', 'dbk_popup_gift_added');
      } catch (error) {
        /* the code is still shown; the visitor can add the Blok themselves */
      }
      return applied;
    }

    copyCode() {
      const button = this.querySelector('[data-copy]');
      const code = this.activeCode || this.dataset.code;
      const done = () => {
        button.textContent = 'Kopieret';
        setTimeout(() => { button.textContent = 'Kopiér'; }, 2000);
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(() => this.selectCode());
      } else {
        this.selectCode();
      }
    }

    selectCode() {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-code-value]'));
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }

    onClose() {
      clearTimeout(this.loadingTimeout);
      // Past the email step they are signed up; closing never counts as a "no thanks".
      if ((this.step === 'question' || this.step === 'email') && !this.finishing) {
        writeStore(localStorage, STORE_KEY, { status: 'dismissed', at: Date.now() });
        this.session.teaser = true;
        this.saveSession();
        this.showTeaser();
      }
      if (this.lastFocus?.isConnected) this.lastFocus.focus({ preventScroll: true });
    }

    showTeaser() {
      // Phones too since 2026-10-06 (Kasper): the visitor keeps a way back to the offer; CSS makes it compact there.
      this.teaser.hidden = !this.session.teaser;
    }

    saveSession() {
      writeStore(sessionStorage, SESSION_KEY, this.session);
    }
  });
}
